import styled from "styled-components";

export const HeaderActionsRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RoleTitleLinkStyled = styled.span`
  font-weight: 600;
  color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  cursor: pointer;
`;

// A plain DOM element (not SbButton) is used as the antd Dropdown's trigger
// deliberately — SbButton isn't ref-forwarding, and antd's Dropdown needs a
// ref-able child to position its overlay; wrapping SbButton in it caused the
// menu to render misaligned. Styled to look identical to the primary
// SbButton it replaces.
export const PlanningToolsTriggerStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 4px 15px;
  border-radius: 6px;
  background: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  color: #fff;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.9;
  }
`;

// Animates a role row's employee panel open/closed like antd's own Collapse
// -- grid-template-rows (0fr <-> 1fr) rather than a fixed height, since that
// would either clip a taller roster or need the panel's real pixel height
// measured in JS up front. The inner div's overflow: hidden + min-height: 0
// are load-bearing: a grid track only shrinks past its content's intrinsic
// height when the child inside it can shrink too, which block content can't
// do on its own. Duration/easing match antd's own motionDurationSlow /
// motionEaseInOut tokens, so this reads as the same animation as elsewhere
// in the app rather than a bespoke one.
export const RolePanelCollapseStyled = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  > div {
    overflow: hidden;
    min-height: 0;
  }
`;

// The accordion panel a role row expands into (see RedeploymentCenterV2's
// identical NestedTableWrapperStyled) — its rows aren't clickable (Role
// Detail is reached from the outer table's Title link instead), so unlike
// that sibling this one doesn't force a pointer cursor on tr.
export const NestedTableWrapperStyled = styled.div`
  padding: 12px 20px;
  background: ${(p) => p.theme?.colors?.light ?? "#F7F7F7"};
  /* This whole panel sits inside the outer role table's own expanded-row
     <tr>, which ClickableTableWrapperStyled (sharedTable.styles.js) forces
     to cursor: pointer — that's inherited by every descendant here (title,
     padding, thead) that doesn't set its own cursor. Setting it here, on the
     panel's own root, gives everything inside a closer ancestor to inherit
     "default" from instead. */
  cursor: default !important;

  .nested-title {
    font-size: 12.5px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  /* Rows aren't clickable here (see this file's header comment), but antd's
     ConfigProvider Table.rowHoverBg token (SbThemeV2's tableRowHoverBg, a
     tan/interactive-looking color) still applies to every table by default,
     which visually implies these rows can be clicked. Swap it for the
     neutral hoverBg wash instead — same fix, same reasoning, as OrgDesign's
     RoleTableWrapperStyled. !important: antd's own injected hover rule lands
     at equal-or-higher specificity, so a plain override won't win. */
  .ant-table-tbody > tr:hover > td {
    background: ${(p) => p.theme?.colors?.hoverBg ?? "#F5F7FA"} !important;
  }

  /* This panel renders inside the outer role table's own tbody (antd's
     expandedRowRender puts it in a <tr class="ant-table-expanded-row"> of
     the parent table), which is itself wrapped in ClickableTableWrapperStyled
     (sharedTable.styles.js) — that wrapper's ".ant-table-tbody > tr { cursor:
     pointer }" is a plain descendant selector, so it also matches this
     nested table's own rows even though they have no onRow. Override it back
     to default here, scoped to this wrapper's own rows. !important: same
     specificity as the outer rule (one generated class + tag), so without it
     which one wins depends on styled-components' injection order rather than
     DOM nesting. */
  .ant-table-tbody > tr {
    cursor: default !important;
  }

  /* antd's default is space-between, which pins the filter icon to the
     column's far (right) edge regardless of how short the header label is
     (see OrgDesign's ImportTableInnerStyled for the same fix). flex-start
     keeps it immediately next to Employee ID / Name's label instead.
     !important: this is fighting AntD's own CSS-in-JS-injected
     justify-content, which lands at equal selector specificity — without it,
     whichever stylesheet was injected later wins, which is order-dependent
     rather than reliable (see sharedTable.styles.js's fuller writeup). */
  .ant-table-filter-column {
    justify-content: flex-start !important;
    gap: 6px;
  }
  /* Open / Discoverable's checkboxes are centered (column align: "center"),
     so their header title + sort icon are centered as a group too, to match —
     scoped to onHeaderCell's className (not every .ant-table-column-sorters)
     so this reliably beats antd's own same-specificity default regardless of
     stylesheet injection order. Same fix, same class name, as the outer role
     table's tm-col-header-center (see sharedTable.styles.js) — duplicated
     here rather than shared since each table's CSS lives with its own
     wrapper. */
  .tm-col-header-center .ant-table-column-sorters {
    justify-content: center !important;
    gap: 6px;
  }
  /* .ant-table-column-title itself defaults to flex: 1 (same AntD-injected,
     equal-specificity rule as above, so it needs the same !important), so it
     keeps stretching to fill the header cell — fighting both rules above (it
     would push the filter icon to the far edge, and stop the sorters group
     from ever looking centered) — cap it to its content width instead. */
  .tm-col-header-center .ant-table-column-title {
    flex: none !important;
  }
`;
